import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactQuill from 'react-quill';
import 'quill/dist/quill.bubble.css';
import {
  BookOpen,
  Eye,
  Loader2,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import {
  getDoctorBlogById,
  listDoctorBlogs,
  type DoctorBlog,
  type DoctorBlogApproval,
} from '../../services/Writer/doctorBlogAdminService';
import { parseQuillOpsToDelta } from '../../utils/quillContent';

type VendorDoctorBlogsPanelProps = {
  vendorId: string;
  doctorName?: string;
};

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
};

const approvalBadgeClass = (approval?: DoctorBlogApproval | null) => {
  switch (approval) {
    case 'approved':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200';
    case 'rejected':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200';
    default:
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200';
  }
};

const statusBadgeClass = (status?: string) =>
  status === 'published'
    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200'
    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';

const BlogViewDrawer: React.FC<{
  blog: DoctorBlog | null;
  loading: boolean;
  onClose: () => void;
}> = ({ blog, loading, onClose }) => {
  if (!blog && !loading) return null;

  const quillValue = blog ? parseQuillOpsToDelta(blog.content || '') : null;
  const categoryName = blog?.category?.name || '—';
  const seoRows = blog
    ? [
        { label: 'Meta Title', value: (blog.metaTitle || '').trim() },
        { label: 'Meta Description', value: (blog.metaDescription || '').trim() },
        {
          label: 'URL Slug',
          value: (blog.slug || '').trim()
            ? `/blog/${String(blog.slug).replace(/^\/?blog\//i, '')}`
            : '',
        },
        { label: 'Focus Keyword', value: (blog.focusKeyword || '').trim() },
        { label: 'Secondary Keywords', value: (blog.secondaryKeywords || '').trim() },
        { label: 'Recommended Schema', value: (blog.recommendedSchema || '').trim() },
      ]
    : [];
  const hasSeo = seoRows.some((row) => Boolean(row.value));

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">
      <button type="button" className="absolute inset-0 bg-black/40" aria-label="Close" onClick={onClose} />
      <aside className="relative flex h-full w-full max-w-3xl flex-col bg-white shadow-2xl dark:bg-gray-900">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Blog details</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : blog ? (
            <div>
              <style
                dangerouslySetInnerHTML={{
                  __html: `
                  .vendor-blog-view .ql-container { border: none !important; font-size: 16px; }
                  .vendor-blog-view .ql-editor { padding: 0 !important; }
                  .dark .vendor-blog-view .ql-editor { color: #f9fafb !important; }
                  .dark .vendor-blog-view .ql-editor * { color: inherit !important; }
                  .dark .vendor-blog-view .ql-editor [style*="color"] { color: inherit !important; }
                  .dark .vendor-blog-view .ql-editor [style*="background"] { background: transparent !important; }
                  .dark .vendor-blog-view a { color: #60a5fa !important; }
                `,
                }}
              />
              {blog.featuredImage ? (
                <div className="flex w-full items-center justify-center bg-gray-100 dark:bg-gray-950">
                  <img
                    src={blog.featuredImage}
                    alt={blog.title}
                    className="max-h-[40vh] w-full object-contain"
                  />
                </div>
              ) : null}
              <div className="space-y-4 p-5 sm:p-6">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className={`rounded-full px-2 py-1 font-semibold capitalize ${statusBadgeClass(blog.status)}`}>
                    {blog.status}
                  </span>
                  <span
                    className={`rounded-full px-2 py-1 font-semibold capitalize ${approvalBadgeClass(
                      blog.adminApproval
                    )}`}
                  >
                    {blog.adminApproval || 'pending'}
                  </span>
                  <span className="text-gray-500 dark:text-gray-400">
                    {formatDate(blog.blogDate || blog.createdAt)}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Category: {categoryName}</p>
                  <h3 className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{blog.title}</h3>
                </div>
                {blog.adminApproval === 'rejected' && (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-200">
                    <div className="font-semibold">Rejected</div>
                    <div className="mt-1">
                      {blog.rejectionReason?.trim()
                        ? `Reason: ${blog.rejectionReason.trim()}`
                        : 'No rejection reason was provided.'}
                    </div>
                  </div>
                )}
                {(blog.tags?.length || blog.referenceLinks?.length) ? (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {blog.tags?.length ? (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Tags</p>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {blog.tags.map((t) => (
                            <span
                              key={t}
                              className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {blog.referenceLinks?.length ? (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">References</p>
                        <ul className="mt-1 space-y-1 text-sm">
                          {blog.referenceLinks.map((link) => (
                            <li key={link}>
                              <a
                                href={link}
                                target="_blank"
                                rel="noreferrer"
                                className="break-all text-primary-600 hover:underline dark:text-primary-400"
                              >
                                {link}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                ) : null}
                <div className="prose max-w-none dark:prose-invert vendor-blog-view">
                  {quillValue ? (
                    <ReactQuill value={quillValue as any} readOnly theme="bubble" modules={{ toolbar: false }} />
                  ) : (
                    <p className="text-sm text-gray-500">No content</p>
                  )}
                </div>
                <div className="border-t border-gray-200 pt-5 dark:border-gray-700">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">SEO Meta Package</h4>
                  {hasSeo ? (
                    <dl className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {seoRows.map((row) => (
                        <div
                          key={row.label}
                          className={`rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/60 ${
                            row.label === 'Meta Description' || row.label === 'Secondary Keywords'
                              ? 'sm:col-span-2'
                              : ''
                          }`}
                        >
                          <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{row.label}</dt>
                          <dd className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-900 dark:text-gray-100">
                            {row.value || '—'}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  ) : (
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      No SEO fields filled for this blog yet.
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </aside>
    </div>,
    document.body
  );
};

const VendorDoctorBlogsPanel: React.FC<VendorDoctorBlogsPanelProps> = ({ vendorId, doctorName }) => {
  const [loading, setLoading] = useState(true);
  const [blogs, setBlogs] = useState<DoctorBlog[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [approvalFilter, setApprovalFilter] = useState<DoctorBlogApproval | ''>('');
  const [statusFilter, setStatusFilter] = useState<'draft' | 'published' | ''>('');
  const [viewBlog, setViewBlog] = useState<DoctorBlog | null>(null);
  const [viewLoading, setViewLoading] = useState(false);

  const loadBlogs = useCallback(async () => {
    if (!vendorId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await listDoctorBlogs({
        authorId: vendorId,
        limit: 100,
        adminApproval: approvalFilter || undefined,
        status: statusFilter || undefined,
        search: search.trim() || undefined,
      });
      setBlogs(result.blogs);
    } catch {
      setError('Failed to load doctor blogs.');
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  }, [vendorId, approvalFilter, statusFilter, search]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadBlogs();
    }, search ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [loadBlogs, search]);

  const categories = useMemo(() => {
    const map = new Map<string, string>();
    blogs.forEach((b) => {
      if (b.categoryId) {
        map.set(b.categoryId, b.category?.name || 'Unknown');
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [blogs]);

  const filteredBlogs = useMemo(() => {
    if (!categoryFilter) return blogs;
    return blogs.filter((b) => b.categoryId === categoryFilter);
  }, [blogs, categoryFilter]);

  const openView = async (blog: DoctorBlog) => {
    setViewBlog(blog);
    setViewLoading(true);
    try {
      const full = await getDoctorBlogById(blog.blogId);
      setViewBlog(full || blog);
    } catch {
      // keep list row as fallback
    } finally {
      setViewLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 px-4 py-3 dark:border-gray-700 sm:px-5">
        <div>
          <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
            <BookOpen className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            Doctor blogs
          </h3>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            {doctorName
              ? `Blogs authored by ${doctorName}`
              : 'All blogs created by this doctor vendor.'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void loadBlogs()}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-700 sm:px-5">
        <div className="relative min-w-[180px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search blog title…"
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          value={approvalFilter}
          onChange={(e) => setApprovalFilter((e.target.value || '') as DoctorBlogApproval | '')}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
        >
          <option value="">All approvals</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter((e.target.value || '') as 'draft' | 'published' | '')}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
        </select>
      </div>

      {error && (
        <div className="mx-4 mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200 sm:mx-5">
          {error}
        </div>
      )}

      {loading && blogs.length === 0 ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : filteredBlogs.length === 0 ? (
        <p className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
          No blogs found for this doctor.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">Title</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">Category</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">Status</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">Approval</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">Updated</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-200">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filteredBlogs.map((blog) => (
                <tr key={blog.blogId} className="hover:bg-gray-50/80 dark:hover:bg-gray-700/30">
                  <td className="max-w-xs px-4 py-3">
                    <div className="font-medium text-gray-900 dark:text-white line-clamp-2">{blog.title}</div>
                    {blog.hasThumbnail || blog.featuredImage ? (
                      <div className="mt-0.5 text-xs text-green-600 dark:text-green-400">Has thumbnail</div>
                    ) : (
                      <div className="mt-0.5 text-xs text-amber-600 dark:text-amber-400">Missing thumbnail</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {blog.category?.name || 'Unknown'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold capitalize ${statusBadgeClass(blog.status)}`}>
                      {blog.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <span
                        className={`inline-flex w-fit rounded-full px-2 py-1 text-xs font-semibold capitalize ${approvalBadgeClass(
                          blog.adminApproval
                        )}`}
                      >
                        {blog.adminApproval || 'pending'}
                      </span>
                      {blog.adminApproval === 'rejected' && blog.rejectionReason ? (
                        <span className="max-w-[200px] text-xs text-red-600 dark:text-red-300 line-clamp-2" title={blog.rejectionReason}>
                          {blog.rejectionReason}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-gray-600 dark:text-gray-300">
                    {formatDate(blog.updatedAt)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => void openView(blog)}
                      className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
                    >
                      <Eye className="h-4 w-4" />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(viewBlog || viewLoading) && (
        <BlogViewDrawer
          blog={viewBlog}
          loading={viewLoading && !viewBlog}
          onClose={() => {
            setViewBlog(null);
            setViewLoading(false);
          }}
        />
      )}
    </div>
  );
};

export default VendorDoctorBlogsPanel;
